import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  fetchUnreadNotifications,
  markNotificationAsReadAsync,
} from '@/features/notifications/notificationSlice'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Bell } from 'lucide-react'
import { EmptyState } from '@/components/ui/empty-state'

/**
 * Notification List Component
 *
 * Scrollable list of notifications.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onClose - Callback to close popover
 */
const NotificationList = ({ onClose }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { notifications, loading } = useSelector((state) => state.notification)

  // Fetch notifications on mount
  useEffect(() => {
    dispatch(fetchUnreadNotifications())
  }, [dispatch])

  // Handle notification click
  const handleClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      dispatch(markNotificationAsReadAsync(notification.id))
    }

    // Navigate to action URL
    if (notification.action_url) {
      navigate(notification.action_url)
      onClose()
    }
  }

  if (loading) {
    return (
      <div role="status" className="p-8 text-center text-body text-ink-2">
        Loading notifications…
      </div>
    )
  }

  if (notifications.length === 0) {
    return (
      <EmptyState icon={Bell} title="You’re all caught up" message="New updates will appear here." size="sm" />
    )
  }

  return (
    <div className="min-h-0 max-h-[min(24rem,60dvh)] overflow-y-auto overscroll-contain">
      {notifications.map((notification) => (
        <button
          key={notification.id}
          type="button"
          onClick={() => handleClick(notification)}
          className={`w-full border-b border-line p-4 text-left transition-colors last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
            !notification.is_read ? 'bg-info-subtle' : ''
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <p className="mb-1 break-words text-body font-medium text-ink [overflow-wrap:anywhere]">
                {notification.title}
              </p>
              <p className="line-clamp-2 break-words text-caption text-ink-2 [overflow-wrap:anywhere]">
                {notification.message}
              </p>
              <p className="text-xs text-ink-2 mt-2">
                {formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                })}
              </p>
            </div>
            {!notification.is_read && (
              <div aria-hidden="true" className="ml-3 mt-1.5 size-1.5 shrink-0 rounded-full bg-brand" />
            )}
          </div>
        </button>
      ))}
    </div>
  )
}

export default NotificationList
